/**
 * MONSTER TOWN — AUDIO ENGINE. HUD/AUDIO/TOUCH CREW (src/audio/**).
 *
 * One AudioContext, three buses, a master gain and a mute the kid can reach.
 *
 * IOS RULES THAT ARE NOT OPTIONAL:
 *  - An AudioContext created before a user gesture starts SUSPENDED and stays
 *    that way. We therefore do not create one at boot; we create it on the first
 *    pointerdown/touchstart/keydown anywhere on the page and immediately play a
 *    one-sample silent buffer, which is what actually unlocks the hardware on
 *    older iPadOS.
 *  - Safari suspends the context when the tab hides and does NOT always resume
 *    it on return, so we resume explicitly on visibilitychange.
 *  - Everything is scheduled a hair in the future (`SCHEDULE_LEAD`) because
 *    scheduling at exactly `currentTime` clicks on older hardware.
 *
 * KID RULES:
 *  - Three volume states on one button: full, quiet, off. One tap cycles. No
 *    slider (no precision dragging), no dead end.
 *  - A rapid-fire tapper cannot stack 40 copies of the same cue into a wall of
 *    noise: identical cues inside RETRIGGER_MS are dropped.
 */

import { layersDuration, makeRng, scheduleLayer, type Layer } from './synth'
import { resolveCue } from './cues'

export type VolumeMode = 'full' | 'quiet' | 'off'

/** Master gain per mode. Deliberately conservative — an iPad speaker is small. */
const MODE_GAIN: Record<VolumeMode, number> = { full: 0.85, quiet: 0.35, off: 0 }

const SCHEDULE_LEAD = 0.012
const RETRIGGER_MS = 45
/** Hard cap on concurrent cue voices. Protects the older iPad from itself. */
const MAX_VOICES = 12

export interface AudioBuses {
  readonly master: GainNode
  readonly sfx: GainNode
  readonly music: GainNode
  readonly voice: GainNode
}

export class AudioEngine {
  private ctx: AudioContext | null = null
  private buses: AudioBuses | null = null
  private mode: VolumeMode = 'full'
  private lastPlayed = new Map<string, number>()
  private voices = 0
  private rng = makeRng(0x51ed)
  private unlockBound = false
  /** True once a real user gesture has created and resumed the context. */
  unlocked = false
  /** Set by the harness so tests are deterministic. */
  seed(n: number): void {
    this.rng = makeRng(n)
  }

  get volumeMode(): VolumeMode {
    return this.mode
  }

  get context(): AudioContext | null {
    return this.ctx
  }

  get running(): boolean {
    return this.ctx !== null && this.ctx.state === 'running'
  }

  // -------------------------------------------------------------------------
  // unlock
  // -------------------------------------------------------------------------

  /**
   * Arm the first-gesture unlock. Listens on `window` (NOT the canvas — only
   * src/app/router.ts may add pointer listeners there) in the capture phase so
   * it fires no matter what swallows the event later.
   */
  armUnlock(): void {
    if (this.unlockBound) return
    this.unlockBound = true
    const kick = (): void => {
      this.ensure()
      void this.ctx?.resume()
      this.silentPing()
      if (this.running) {
        this.unlocked = true
        window.removeEventListener('pointerdown', kick, true)
        window.removeEventListener('touchstart', kick, true)
        window.removeEventListener('keydown', kick, true)
      }
    }
    window.addEventListener('pointerdown', kick, { capture: true, passive: true })
    window.addEventListener('touchstart', kick, { capture: true, passive: true })
    window.addEventListener('keydown', kick, { capture: true, passive: true })

    document.addEventListener('visibilitychange', () => {
      if (this.ctx === null) return
      if (document.visibilityState === 'hidden') void this.ctx.suspend()
      else if (this.mode !== 'off') void this.ctx.resume()
    })
  }

  /** The actual iOS hardware unlock: a one-sample silent buffer. */
  private silentPing(): void {
    const ctx = this.ctx
    if (ctx === null) return
    try {
      const b = ctx.createBuffer(1, 1, ctx.sampleRate)
      const s = ctx.createBufferSource()
      s.buffer = b
      s.connect(ctx.destination)
      s.start(0)
    } catch {
      /* a browser that refuses this is a browser we cannot help */
    }
  }

  /** Create the context + bus graph on demand. Safe to call repeatedly. */
  ensure(): AudioBuses | null {
    if (this.buses !== null) return this.buses
    const Ctor: typeof AudioContext | undefined =
      typeof AudioContext !== 'undefined'
        ? AudioContext
        : (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (Ctor === undefined) return null
    let ctx: AudioContext
    try {
      ctx = new Ctor({ latencyHint: 'interactive' })
    } catch {
      return null
    }
    const master = ctx.createGain()
    master.gain.value = MODE_GAIN[this.mode]
    master.connect(ctx.destination)

    const mk = (g: number): GainNode => {
      const node = ctx.createGain()
      node.gain.value = g
      node.connect(master)
      return node
    }
    // Music sits well under sfx: the bed should never fight the reaction to a tap.
    this.buses = { master, sfx: mk(1), music: mk(0.34), voice: mk(1) }
    this.ctx = ctx
    return this.buses
  }

  // -------------------------------------------------------------------------
  // volume
  // -------------------------------------------------------------------------

  setVolumeMode(mode: VolumeMode): void {
    this.mode = mode
    const b = this.buses
    if (b === null || this.ctx === null) return
    const t = this.ctx.currentTime
    b.master.gain.cancelScheduledValues(t)
    b.master.gain.setTargetAtTime(MODE_GAIN[mode], t, 0.05)
    if (mode === 'off') void this.ctx.suspend()
    else void this.ctx.resume()
  }

  /** One tap cycles full -> quiet -> off -> full. No slider, no dead end. */
  cycleVolume(): VolumeMode {
    const next: VolumeMode = this.mode === 'full' ? 'quiet' : this.mode === 'quiet' ? 'off' : 'full'
    this.setVolumeMode(next)
    return next
  }

  /** Duck the music under a voice line or a celebration, then let it back up. */
  duck(seconds: number, to = 0.3): void {
    const b = this.buses
    if (b === null || this.ctx === null) return
    const t = this.ctx.currentTime
    b.music.gain.cancelScheduledValues(t)
    b.music.gain.setTargetAtTime(0.34 * to, t, 0.08)
    b.music.gain.setTargetAtTime(0.34, t + seconds, 0.35)
  }

  // -------------------------------------------------------------------------
  // playback
  // -------------------------------------------------------------------------

  /**
   * Play a cue id. Unknown ids resolve to a generic pop (see `resolveCue`) —
   * a crew shipping a sound before the table catches up gets a reaction, not
   * silence.
   */
  play(cueId: string, volume = 1): boolean {
    if (this.mode === 'off') return false
    const buses = this.ensure()
    if (buses === null || this.ctx === null) return false
    if (this.ctx.state !== 'running') {
      // Not unlocked yet. Do not queue: a burst of stale sounds on unlock is
      // worse than the first tap being quiet.
      void this.ctx.resume()
      return false
    }
    const now = performance.now()
    const last = this.lastPlayed.get(cueId)
    if (last !== undefined && now - last < RETRIGGER_MS) return false
    if (this.voices >= MAX_VOICES) return false
    this.lastPlayed.set(cueId, now)

    const def = resolveCue(cueId)
    const t0 = this.ctx.currentTime + SCHEDULE_LEAD
    const dur = layersDuration(def.layers)

    const g = this.ctx.createGain()
    g.gain.value = Math.max(0, volume) * (def.gain ?? 1)
    g.connect(buses.sfx)

    // Per-play pitch wobble keeps the 200th block placement from sounding like
    // a machine. Cents, tiny, deterministic under the harness seed.
    const detuneCents = def.wobble === undefined || def.wobble === 0 ? 0 : (this.rng() * 2 - 1) * def.wobble
    const pitch = Math.pow(2, detuneCents / 1200)
    for (const layer of def.layers) {
      scheduleLayer(this.ctx, g, pitchShift(layer, pitch), t0, this.rng)
    }

    this.voices++
    window.setTimeout(() => {
      this.voices = Math.max(0, this.voices - 1)
      try {
        g.disconnect()
      } catch {
        /* already gone */
      }
    }, (dur + 0.2) * 1000)
    return true
  }

  dispose(): void {
    if (this.ctx !== null) void this.ctx.close()
    this.ctx = null
    this.buses = null
  }
}

/**
 * Apply a per-play pitch multiplier. Returns a NEW layer — the cue table is
 * data and must never be mutated by playback.
 */
function pitchShift(layer: Layer, mul: number): Layer {
  if (mul === 1) return layer
  switch (layer.kind) {
    case 'tone':
      return {
        ...layer,
        freq: layer.freq * mul,
        ...(layer.toFreq === undefined ? {} : { toFreq: layer.toFreq * mul }),
      }
    case 'noise':
      return {
        ...layer,
        ...(layer.freq === undefined ? {} : { freq: layer.freq * mul }),
        ...(layer.toFreq === undefined ? {} : { toFreq: layer.toFreq * mul }),
      }
    case 'pluck':
      return { ...layer, freq: layer.freq * mul }
    case 'chime':
      return { ...layer, freqs: layer.freqs.map((f) => f * mul) }
    case 'chatter':
      return { ...layer, low: layer.low * mul, high: layer.high * mul }
  }
}

export const audio = new AudioEngine()
